/**
 * Settings → Connectors, in two sections.
 *
 * "Remote servers" lists the person's remote MCP servers, reached by the
 * Worker so they work from the browser (`GET /connectors`). A card's tools
 * only show once it is `connected`; `needs_auth` offers a sign-in that hands
 * off to the server's OAuth page, and `error` offers a retry. The OAuth round
 * trip lands back here as `?signin=...`, read once on mount and cleared with
 * `navigate(..., { replace: true })` so a reload does not replay it.
 *
 * "On this computer", in the desktop app only, lists its local MCP servers
 * (docs/desktop-bridge.md). They are added, and their tools allowed, in the
 * app's own settings, which the buttons open: adding one runs a program.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  addConnector,
  authorizeConnector,
  deleteConnector,
  listConnectors,
  patchConnector,
  refreshConnector,
  type Connector,
  type ConnectorTool,
  type PermLevel
} from '../../api';
import { desktopServers, inDesktop, onDesktopChange, openDesktopSettings, type DesktopServer } from '../../desktop';
import { ds } from '../../ds';
import { useRouter } from '../../router';
import { IconTap, MonoLabel, Ph } from '../ui';

const LEVELS: readonly PermLevel[] = ['allow', 'ask', 'block'];
const LEVEL_LABELS: Record<PermLevel, string> = { allow: 'Allow', ask: 'Ask', block: 'Block' };

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

const SERVER_STATE: Record<DesktopServer['state'], string> = {
  running: '',
  starting: 'Starting',
  failed: 'Did not start',
  off: 'Off'
};

/** The desktop app's local MCP servers. */
function ThisComputer() {
  const { Button } = ds();
  const [servers, setServers] = useState<DesktopServer[] | null>(null);

  useEffect(() => {
    const load = () => void desktopServers().then(setServers);
    load();
    return onDesktopChange(load);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <MonoLabel>On this computer</MonoLabel>
      {servers?.length ? (
        <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
          {servers.map((s, i) => (
            <div
              key={s.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                ...(i ? { borderTop: '1px solid var(--border-subtle)' } : {})
              }}
            >
              <Ph name="plugs-connected" size={16} style={{ color: 'var(--text-muted)' }} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>{s.name}</span>
              <span style={{ fontSize: 12.5, color: s.state === 'failed' ? 'var(--danger-600)' : 'var(--text-muted)' }}>
                {s.state === 'running' ? `${s.tools} ${s.tools === 1 ? 'tool' : 'tools'}` : SERVER_STATE[s.state]}
              </span>
            </div>
          ))}
        </div>
      ) : servers ? (
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>No servers on this computer.</p>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <Button variant="neutral" density="app" size="sm" onClick={() => void openDesktopSettings('servers')}>
          {servers?.length ? 'Manage servers' : 'Add a server'}
        </Button>
        <Button variant="ghost" density="app" size="sm" onClick={() => void openDesktopSettings('folders')}>
          Choose folders
        </Button>
      </div>
    </div>
  );
}

function LevelSwitch({
  value,
  disabled,
  onPick
}: {
  value: PermLevel | undefined;
  disabled: boolean;
  onPick: (level: PermLevel) => void;
}) {
  return (
    <div style={{ display: 'flex', padding: 2, borderRadius: 'var(--radius-sm)', background: 'var(--app-panel-sunken)' }}>
      {LEVELS.map((level) => {
        const on = value === level;
        return (
          <button
            key={level}
            type="button"
            className="bare"
            disabled={disabled}
            onClick={() => onPick(level)}
            style={{
              height: 28,
              display: 'flex',
              alignItems: 'center',
              padding: '0 12px',
              borderRadius: 6,
              fontSize: 12.5,
              background: on ? 'var(--surface-card)' : 'transparent',
              color: on ? 'var(--text-strong)' : 'var(--text-muted)',
              boxShadow: on ? 'var(--shadow-xs)' : 'none'
            }}
          >
            {LEVEL_LABELS[level]}
          </button>
        );
      })}
    </div>
  );
}

function ToolRow({
  tool,
  disabled,
  onPick
}: {
  tool: ConnectorTool;
  disabled: boolean;
  onPick: (level: PermLevel) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '8px 16px 8px 60px',
        borderTop: '1px solid var(--border-subtle)'
      }}
    >
      <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 13.5, color: 'var(--text-body)' }}>{tool.title ?? tool.name}</span>
        {tool.description ? (
          <span
            style={{
              fontSize: 12.5,
              color: 'var(--text-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {tool.description}
          </span>
        ) : null}
      </div>
      <LevelSwitch value={tool.level} disabled={disabled} onPick={onPick} />
    </div>
  );
}

function ConnectorCard({
  connector,
  busy,
  removing,
  onSignIn,
  onRetry,
  onPickLevel,
  onAskRemove,
  onCancelRemove,
  onRemove
}: {
  connector: Connector;
  busy: boolean;
  removing: boolean;
  onSignIn: () => void;
  onRetry: () => void;
  onPickLevel: (toolName: string, level: PermLevel) => void;
  onAskRemove: () => void;
  onCancelRemove: () => void;
  onRemove: () => void;
}) {
  const { Button } = ds();
  return (
    <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '14px 16px' }}>
        <span
          style={{
            width: 32,
            height: 32,
            flex: 'none',
            borderRadius: 9,
            background: 'var(--primary-soft)',
            color: 'var(--primary)',
            display: 'grid',
            placeItems: 'center'
          }}
        >
          <Ph name="plugs-connected" size={16} />
        </span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>{connector.name}</span>
          <span
            style={{
              fontSize: 12.5,
              color: 'var(--text-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {hostOf(connector.url)}
          </span>
        </div>
        {connector.status === 'needs_auth' ? (
          <button
            type="button"
            className="bare hov-strong"
            disabled={busy}
            onClick={onSignIn}
            style={{ fontSize: 13, color: 'var(--text-link)' }}
          >
            Sign in
          </button>
        ) : null}
        {connector.status === 'error' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>Could not reach it</span>
            <button
              type="button"
              className="bare hov-strong"
              disabled={busy}
              onClick={onRetry}
              style={{ fontSize: 13, color: 'var(--text-link)' }}
            >
              Try again
            </button>
          </div>
        ) : null}
        <IconTap icon="trash" label={`Remove ${connector.name}`} onClick={onAskRemove} disabled={busy} />
      </div>

      {removing ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 16px',
            borderTop: '1px solid var(--border-subtle)'
          }}
        >
          <span style={{ flex: 1, fontSize: 13.5, color: 'var(--text-body)' }}>Remove this connector?</span>
          <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={onCancelRemove}>
            Cancel
          </Button>
          <Button variant="danger" density="app" size="sm" disabled={busy} onClick={onRemove}>
            Remove
          </Button>
        </div>
      ) : connector.status === 'connected' ? (
        connector.tools.map((tool) => (
          <ToolRow key={tool.name} tool={tool} disabled={busy} onPick={(level) => onPickLevel(tool.name, level)} />
        ))
      ) : null}
    </div>
  );
}

export function ConnectorsTab() {
  const { query, navigate } = useRouter();
  const { Button, Input } = ds();

  const [connectors, setConnectors] = useState<Connector[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [signinFailed, setSigninFailed] = useState(false);

  const [addUrl, setAddUrl] = useState('');
  const [addName, setAddName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const load = useCallback(() => {
    listConnectors()
      .then((r) => setConnectors(r.connectors))
      .catch(() => setConnectors([]));
  }, []);

  useEffect(() => load(), [load]);

  useEffect(() => {
    const signin = query.get('signin');
    if (!signin) return;
    if (signin === 'failed') setSigninFailed(true);
    navigate('/settings/connectors', { replace: true });
    // Only the query the tab was opened with matters, and only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateConnector = (updated: Connector) =>
    setConnectors((cs) => (cs ?? []).map((c) => (c.id === updated.id ? updated : c)));

  const signIn = (connector: Connector) => {
    if (busyId) return;
    setBusyId(connector.id);
    authorizeConnector(connector.id)
      .then(({ authorizeUrl }) => window.location.assign(authorizeUrl))
      .catch(() => setBusyId(null));
  };

  const retry = (connector: Connector) => {
    if (busyId) return;
    setBusyId(connector.id);
    refreshConnector(connector.id).then(updateConnector).catch(() => {}).finally(() => setBusyId(null));
  };

  const pickLevel = (connector: Connector, toolName: string, level: PermLevel) => {
    if (busyId || connector.tools.find((t) => t.name === toolName)?.level === level) return;
    setBusyId(connector.id);
    patchConnector(connector.id, { levels: { [toolName]: level } })
      .then(updateConnector)
      .catch(() => {})
      .finally(() => setBusyId(null));
  };

  const remove = (connector: Connector) => {
    if (busyId) return;
    setBusyId(connector.id);
    deleteConnector(connector.id)
      .then(() => {
        setConnectors((cs) => (cs ?? []).filter((c) => c.id !== connector.id));
        setRemovingId(null);
      })
      .catch(() => {})
      .finally(() => setBusyId(null));
  };

  const add = () => {
    const url = addUrl.trim();
    if (!url || adding) return;
    setAdding(true);
    setAddError(null);
    addConnector({ url, ...(addName.trim() ? { name: addName.trim() } : {}) })
      .then((connector) => {
        setConnectors((cs) => [...(cs ?? []), connector]);
        setAddUrl('');
        setAddName('');
      })
      .catch((err: unknown) => setAddError(err instanceof ApiError ? err.message : 'That did not go through. Try again.'))
      .finally(() => setAdding(false));
  };

  const showEmpty = connectors !== null && connectors.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {inDesktop() ? <ThisComputer /> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <MonoLabel>Remote servers</MonoLabel>

        {signinFailed ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>Sign-in did not finish.</span> : null}
        {showEmpty ? (
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>Add a remote MCP server by its address.</p>
        ) : null}

        {(connectors ?? []).map((connector) => (
          <ConnectorCard
            key={connector.id}
            connector={connector}
            busy={busyId === connector.id}
            removing={removingId === connector.id}
            onSignIn={() => signIn(connector)}
            onRetry={() => retry(connector)}
            onPickLevel={(toolName, level) => pickLevel(connector, toolName, level)}
            onAskRemove={() => setRemovingId(connector.id)}
            onCancelRemove={() => setRemovingId(null)}
            onRemove={() => remove(connector)}
          />
        ))}

        <div
          style={{
            background: 'var(--surface-card)',
            borderRadius: 'var(--radius-app-card)',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 2, minWidth: 220 }}>
              <Input placeholder="https://…" value={addUrl} onChange={(e) => setAddUrl(e.target.value)} disabled={adding} />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <Input
                placeholder="Name (optional)"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                disabled={adding}
              />
            </div>
            <Button variant="primary" density="app" size="sm" disabled={adding || !addUrl.trim()} onClick={add}>
              Add
            </Button>
          </div>
          {addError ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{addError}</span> : null}
        </div>
      </div>
    </div>
  );
}
